import React from "react";
import { Container, Row, Col, Table } from "reactstrap";

const PrintComponent = React.forwardRef(({ branch, data, logoUrl: propLogoUrl }, ref) => {
  console.log("PrintComponent data:", branch);
  // Safe Date Formatter (DD/MM/YYYY)
  const formatDisplayDate = (dateVal) => {
    if (!dateVal) return "--/--/----";
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return "--/--/----";  
    return `${String(d.getDate()).padStart(2, "0")}/${String(
      d.getMonth() + 1
    ).padStart(2, "0")}/${d.getFullYear()}`;
  };

  // Safe Time Formatter (hh:mm AM/PM)
  const formatDisplayTime = (fullDateVal) => {
    const d = fullDateVal ? new Date(fullDateVal) : new Date();
    if (isNaN(d.getTime())) {
      return new Date().toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    }
    return d.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Extract logoUrl from props, branch data, or main data object
  const resolvedLogoUrl =
    propLogoUrl ||
    branch?.logoUrl ||
    branch?.logo ||
    data?.logoUrl ||
    data?.branchData?.logoUrl ||
    null;

  // Safe patient extraction across multiple object structures
  const patientFirstName =
    data?.fname || data?.selectedCm?.fname || data?.customerData?.fname  ||"";
  const patientLastName =
    data?.sname || data?.selectedCm?.sname || data?.customerData?.sname || "";
  const patientFullName =
    `${patientFirstName} ${patientLastName}`.trim() || data?.patientName || "Patient Record";

  const patientContact =
    data?.contactno ||
    data?.contactNo ||
    data?.selectedCm?.contactno ||
    data?.selectedCm?.contactNo ||
    data?.customerData?.contactno ||
    "N/A";

  const patientMrn =
    data?.mrn || data?.selectedCm?.mrn || data?.customerData?.mrn || "N/A";
  const patientAge =
    data?.age || data?.selectedCm?.age || data?.customerData?.age || "—";
  const patientSex =
    data?.sex || data?.selectedCm?.sex || data?.customerData?.sex || "—";

  // Safe Doctor Name Extraction across all possible paths
  const doctorName =
    data?.doctorName ||
    data?.username ||
    data?.userData?.user ||
    data?.userData?.name ||
    "Doctor";

  // Branch Address construction
  const addressText =
    typeof branch?.address === "object"
      ? [
          branch?.address?.first_line,
          branch?.address?.second_line,
          branch?.address?.third_line,
          branch?.address?.fourth_line,
        ]
          .filter(Boolean)
          .join(", ")
      : branch?.address || "";

  // Structured medication array helper
  const medsArray = Array.isArray(data?.prescribedMedications)
    ? data.prescribedMedications
    : [];

  return (
    <div ref={ref} className="print-container">
      <style>
        {`
        @media print {
          @page {
            size: A4;
            margin: 12mm;
          }
          body {
            font-family: Arial, Helvetica, sans-serif;
            font-size: 11px;
            color: #000000;
            background: #ffffff !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .no-print {
            display: none !important;
          }
          img {
            max-width: 100% !important;
            display: block !important;
          }
        }
        .print-wrapper {
          padding: 10px;
          background: #ffffff;
          color: #000000;
        }
        .header-line {
          border-bottom: 2px solid #000000;
          padding-bottom: 8px;
          margin-bottom: 12px;
        }
        .patient-box {
          border: 1px solid #000000;
          padding: 8px 12px;
          margin-bottom: 14px;
        }
        .section-heading {
          font-size: 11px;
          font-weight: bold;
          text-transform: uppercase;
          border-bottom: 1px solid #000000;
          padding-bottom: 2px;
          margin-top: 10px;
          margin-bottom: 6px;
        }
        .section-body {
          font-size: 11px;
          line-height: 1.4;
          margin-bottom: 10px;
          white-space: pre-wrap;
        }
        .med-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 12px;
        }
        .med-table th, .med-table td {
          border: 1px solid #000000;
          padding: 5px 8px;
          font-size: 10.5px;
          text-align: left;
        }
        .med-table th {
          font-weight: bold;
          background-color: transparent !important;
        }
      `}
      </style>

      <div className="print-wrapper">
        {/* Header & Logo */}
      <div className="header-line">
  <Row className="align-items-center justify-content-between">
    {/* Left: Branch/Clinic Logo */}
    <Col xs="3" className="d-flex align-items-center">
      {resolvedLogoUrl ? (
        <img
          src={resolvedLogoUrl}
          alt="Clinic Logo"
          style={{
            maxHeight: "65px",
            maxWidth: "160px",
            objectFit: "contain",
            display: "block",
          }}
          onError={(e) => {
            e.target.style.display = "none";
          }}
        />
      ) : (
        <span
          style={{
            fontSize: "12px",
            fontWeight: "700",
            letterSpacing: "0.5px",
            textTransform: "uppercase",
            color: "#333",
          }}
        >
          {branch?.name || "CLINIC"}
        </span>
      )}
    </Col>

    {/* Center: Centered Primary Header */}
    <Col xs="6" className="text-center">
      <h5
        style={{
          fontWeight: "800",
          margin: 0,
          fontSize: "16px",
          letterSpacing: "1px",
          textTransform: "uppercase",
          color: "#000",
        }}
      >
        MEDICAL PRESCRIPTION
      </h5>
      <span
        style={{
          display: "inline-block",
          fontSize: "10px",
          fontWeight: "600",
          letterSpacing: "1.5px",
          textTransform: "uppercase",
          color: "#555",
          marginTop: "2px",
          borderBottom: "1px solid #ccc",
          paddingBottom: "1px",
        }}
      >
        Consultation Record
      </span>
    </Col>

    {/* Right: Branch Address & Info */}
    <Col xs="3" className="text-end">
      <p
        style={{
          margin: 0,
          fontSize: "9.5px",
          lineHeight: "1.3",
          color: "#222",
          wordBreak: "break-word",
        }}
      >
        {addressText || "Branch Address"}
      </p>
    </Col>
  </Row>
</div>

        {/* Patient & Doctor Information Matrix */}
        <div className="patient-box">
          <Row>
            <Col xs="6">
              <div>
                <strong>Patient Name:</strong> {patientFullName}
              </div>
              <div>
                <strong>MRN:</strong> {patientMrn} | <strong>Age/Gender:</strong>{" "}
                {patientAge} / {patientSex}
              </div>
              <div>
                <strong>Contact No:</strong> {patientContact}
              </div>
            </Col>
            <Col xs="6" className="text-end">
              <div>
                <strong>Consulting Doctor:</strong> {doctorName}
              </div>
              <div>
                <strong>Date:</strong>{" "}
                {formatDisplayDate(data?.date || data?.fullDate)}
              </div>
              <div>
                <strong>Time:</strong> {formatDisplayTime(data?.fullDate)}
              </div>
            </Col>
          </Row>
        </div>

        {/* Complaints & History */}
        <div>
          <div className="section-heading">1. Complaints & History</div>
          <div className="section-body">
            {data?.complaintsnHistory || "None recorded"}
          </div>
        </div>

        {/* Diagnosis */}
        <div>
          <div className="section-heading">2. Diagnosis</div>
          <div className="section-body">
            {data?.diagnosis || "None recorded"}
          </div>
        </div>

        {/* Prescribed Medications */}
        <div>
          <div className="section-heading">3. Rx / Prescribed Medications</div>
          {medsArray.length > 0 ? (
            <table className="med-table">
              <thead>
                <tr>
                  <th style={{ width: "35%" }}>Medicine Name</th>
                  <th style={{ width: "20%" }}>Dosage</th>
                  <th style={{ width: "20%" }}>Timing</th>
                  <th style={{ width: "25%" }}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {medsArray.map((med, index) => (
                  <tr key={index}>
                    <td>
                      <strong>
                        {med.medicine?.label || med.medicine?.value || "—"}
                      </strong>
                    </td>
                    <td>{med.dosage || "—"}</td>
                    <td>{med.timing || "—"}</td>
                    <td>{med.customNotes || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="section-body">
              {data?.prescriptionNotes ||
                data?.prescription ||
                "No prescription directives added."}
            </div>
          )}
        </div>

        {/* Special Advice */}
        <div>
          <div className="section-heading">4. Special Advice & Instructions</div>
          <div className="section-body">
            {data?.specialAdvice || "None"}
          </div>
        </div>

        {/* Doctor Signature Block */}
        <Row style={{ marginTop: "40px" }} className="align-items-end">
          <Col xs="6" style={{ fontSize: "9px" }}>
            * This is a computer-generated prescription report.
          </Col>
          <Col xs="6" className="text-end">
            <div
              style={{
                display: "inline-block",
                textAlign: "center",
                borderTop: "1px solid #000",
                paddingTop: "4px",
                minWidth: "160px",
              }}
            >
              <strong>{doctorName}</strong>
              <div style={{ fontSize: "9px" }}>Doctor's Signature</div>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
});

export default PrintComponent;